"use client";

import { useState } from "react";
import Image from "next/image";
import { Controller, useForm } from "react-hook-form";
import { useRouter } from "next/navigation";
import clsx from "clsx";
import Button from "@/components/common/Button";
import TextField from "@/components/common/TextField";
import Select from "@/components/common/Select";
import ScrollReveal from "@/components/common/ScrollReveal";
import {
  InterestGroup,
  PRODUCT_OPTIONS,
  SERVICE_OPTIONS,
} from "@/components/Contact/RequestDemoForm";
import { countries } from "@/lib/constants";
import { getApiErrorMessage } from "@/lib/apiError";
import {
  RECAPTCHA_FAILED_MESSAGE,
  getRecaptchaToken,
  isRecaptchaFailure,
} from "@/lib/recaptcha";
import { ContactFormData, Location } from "@/types/constants_type";

/** Company name lives in UI state only (folded into `message` on submit). */
interface ContactFormValues extends ContactFormData {
  companyName: string;
}

interface ContactFormProps {
  location: Location;
  successHref?: string;
}

/**
 * Contact page "Leave us a message" card. The inner form mirrors the
 * Request-a-Demo form (RequestDemoForm) — two-column Name/Email/Company/Country,
 * two expandable multi-select interest groups, and a Message field — but keeps
 * the Contact card shell (gradient + fingerprint + "Leave us a message" title)
 * and mails to the selected office (`location.mail`). Interests are serialized
 * into `inquiry` and Company Name is folded into `message`, same as the demo.
 */
export default function ContactForm({
  location,
  successHref = "/contact/thank-you",
}: ContactFormProps) {
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(
    new Set()
  );
  const [selectedServices, setSelectedServices] = useState<Set<string>>(
    new Set()
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ContactFormValues>({
    mode: "onBlur",
    defaultValues: {
      name: "",
      email: "",
      companyName: "",
      country: "",
      inquiry: "",
      message: "",
    },
  });

  const toggleFromSet = (
    setState: React.Dispatch<React.SetStateAction<Set<string>>>,
    value: string
  ) => {
    clearErrors("inquiry");
    setState((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const onSubmit = async (data: ContactFormValues) => {
    const parts: string[] = [];
    if (selectedProducts.size > 0) {
      parts.push(`Our Product: ${Array.from(selectedProducts).join(", ")}`);
    }
    if (selectedServices.size > 0) {
      parts.push(`Our Services: ${Array.from(selectedServices).join(", ")}`);
    }
    const inquiry = parts.join(" | ");

    // The interest groups are local state, not registered fields, so they need
    // their own required check — otherwise `inquiry` posts as an empty string.
    if (!inquiry) {
      setError("inquiry", {
        type: "required",
        message: "Please select at least one product or service",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    const message = data.companyName.trim()
      ? `Company: ${data.companyName.trim()}\n\n${data.message}`
      : data.message;

    /** Posts the form with a brand-new token — the backend refuses a reused
     *  or >120 s-old one with the same "Invalid reCAPTCHA" error. */
    const post = async () => {
      const token = await getRecaptchaToken();

      const response = await fetch(
        "https://diana.e-cq.net/api/v1/users/contact",
        {
          method: "POST",
          cache: "no-cache",
          headers: { "Content-Type": "application/json" },
          redirect: "follow",
          referrerPolicy: "no-referrer",
          body: JSON.stringify({
            name: data.name,
            email: data.email,
            country: data.country,
            inquiry,
            mailTo: location.mail,
            message,
            recaptchaResponse: token,
          }),
        }
      );

      return response.json();
    };

    try {
      let result = await post();

      // Captcha refused (often a stale token) → retry once, never the same token.
      if (isRecaptchaFailure(result)) {
        result = await post();
      }

      if (result?.status === "success") {
        reset();
        router.push(successHref);
      } else if (isRecaptchaFailure(result)) {
        // Hide the backend's raw "Oops! Invalid reCAPTCHA" wording.
        setSubmitError(RECAPTCHA_FAILED_MESSAGE);
      } else {
        // Show the backend's validation/error message instead of a generic one.
        setSubmitError(getApiErrorMessage(result));
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setSubmitError(
        // A missing script / empty token throws out of getRecaptchaToken().
        isRecaptchaFailure(err)
          ? RECAPTCHA_FAILED_MESSAGE
          : "Something went wrong! Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ScrollReveal direction="up" delay={250}>
      <div
        className={clsx(
          "relative w-full flex flex-col items-center gap-5 sm:gap-8",
          "bg-gradient-to-b from-[#2e353a] from-[13.725%] to-[#15181d]",
          "rounded-tl-[48px] rounded-tr-[48px]",
          // The top padding only exists to clear the fingerprint that overhangs
          // the card, so it shrinks in lock-step with it below sm:
          // 102 - 71 = 31px clearance today, 72 - 48 = 24px on phones.
          "pt-[72px] pb-10 px-5 sm:pt-[102px] sm:pb-16 sm:px-8"
        )}
      >
        {/* Fingerprint (unchanged — Contact-specific decoration). */}
        <div className="absolute left-1/2 -translate-x-1/2 -top-[48px] size-[96px] sm:-top-[71px] sm:size-[142px]">
          <Image
            src="/images/Contact/fingerprint.svg"
            alt=""
            width={142}
            height={142}
            className="w-full h-full"
            priority
          />
        </div>

        <h3 className="headline-4 text-text-highlight text-center w-full">
          Leave us a message
        </h3>

        {submitError && (
          <div className="w-full p-3 rounded-lg bg-primary-600/10 border border-primary-600 text-primary-600">
            <p className="text-center font-normal text-[14px] leading-[20px]">
              {submitError}
            </p>
          </div>
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full flex flex-col gap-6"
          noValidate
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
            <TextField
              label="Name"
              required
              placeholder=""
              errorMessage={errors.name?.message}
              {...register("name", {
                required: "This is required field",
                minLength: {
                  value: 2,
                  message: "Name must be at least 2 characters",
                },
              })}
            />
            <TextField
              label="Email"
              required
              placeholder=""
              errorMessage={errors.email?.message}
              {...register("email", {
                required: "This is required field",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
            />

            <TextField
              label="Company Name"
              placeholder=""
              {...register("companyName")}
            />

            <div className="flex flex-col items-start w-full">
              <div className="flex items-center px-4 leading-7 text-[16px] tracking-[0.5px] font-[var(--font-avenir)] text-neutral-300">
                <span>Country</span>
                <span>*</span>
              </div>
              <Controller
                name="country"
                control={control}
                rules={{ required: "This is required field" }}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    placeholder=""
                    error={errors.country?.message}
                    items={countries.map((c) => ({
                      value: c.value,
                      label: c.label,
                    }))}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex flex-col items-start w-full gap-4">
            <p className="px-4 leading-7 text-[16px] tracking-[0.5px] font-[var(--font-avenir)] text-neutral-300">
              Select what you&rsquo;re interested in
            </p>
            <InterestGroup
              title="Our Product"
              options={PRODUCT_OPTIONS}
              selected={selectedProducts}
              onToggleItem={(value) => toggleFromSet(setSelectedProducts, value)}
            />
            <InterestGroup
              title="Our Services"
              options={SERVICE_OPTIONS}
              selected={selectedServices}
              onToggleItem={(value) => toggleFromSet(setSelectedServices, value)}
            />
            {errors.inquiry?.message && (
              <div className="flex items-center px-4">
                <p className="text-primary-600 text-[12px] leading-5 tracking-[0.25px] font-[var(--font-avenir)]">
                  {errors.inquiry.message}
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col items-start w-full">
            {/* Asterisk to match Name/Email/Country — the field is registered
                `required`, so the label has to say so too. */}
            <div className="flex items-center px-4 leading-7 text-[16px] tracking-[0.5px] font-[var(--font-avenir)] text-neutral-300">
              <span>Message</span>
              <span>*</span>
            </div>
            <div
              className={clsx(
                // mx-4, not px-4 — the underline is on this element and has to stay
                // on the same 16px inset as the label/value/error text.
                "flex items-center self-stretch mx-4 py-1 border-b transition-colors",
                errors.message ? "border-primary-600" : "border-neutral-500"
              )}
            >
              <textarea
                {...register("message", {
                  required: "This is required field",
                  minLength: {
                    value: 5,
                    message: "Message must be at least 5 characters",
                  },
                })}
                rows={2}
                placeholder=""
                className={clsx(
                  "flex-1 min-w-0 bg-transparent outline-none border-none",
                  "font-[var(--font-avenir)] text-text-default text-[16px] leading-7 tracking-[0.5px]",
                  "placeholder:text-neutral-500 resize-none"
                )}
              />
            </div>
            {errors.message && (
              <p className="px-4 mt-2 text-primary-600 text-[12px] leading-5 tracking-[0.25px] font-[var(--font-avenir)]">
                {errors.message.message}
              </p>
            )}
          </div>

          <p className="body-1 text-text-default text-center w-full">
            For urgent support, please contact our local hotline.
          </p>

          <div className="flex items-center justify-center w-full pt-2">
            <Button
              variant="fill"
              color="primary"
              type="submit"
              width={244}
              height={64}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </Button>
          </div>
        </form>
      </div>
    </ScrollReveal>
  );
}
